import { Provider } from '@ethersproject/providers';
import { fetchBlockTimestamp } from 'api/blockchain/blockTimestamp';
import { Crucible, ExtendedRewardProgram, Subscription } from 'types';
import { CRUCIBLES_STAKES_QUERY } from 'helpers/queries';
import { hasSeasonalNFT } from 'helpers/SeasonalNFT';
import { Config } from 'store/config';
import { UsdValues } from 'types/firebase';
import { CrucibleEntity, CrucibleGraphData, Lock } from 'types/NFT';
import { formatEther, formatUnits } from 'ethers/lib/utils';
import { BigNumber, ethers } from 'ethers';
import bigNumberishToNumber from './bigNumberishToNumber';
import IUniswapV2ERC20 from '@uniswap/v2-core/build/IUniswapV2ERC20.json';

// Get ETH balance in crucible
export async function getEthBalance(crucible: Crucible, provider: Provider) {
  const ethBalanceBN = await provider.getBalance(crucible.id);
  const ethBalanceNumber = Number(formatEther(ethBalanceBN));
  return ethBalanceNumber;
}

// Get current timestamp from server
export async function getTimestamp(chainId: number) {
  const ts = await fetchBlockTimestamp(chainId);
  return ts;
}

// Get christmas flag for artwork
export async function getChristmasFlag(
  config: Config,
  crucible: Crucible,
  provider: Provider
) {
  if (config.seasonalNFTAddress && config.christmasTokenId) {
    return await hasSeasonalNFT(
      config.seasonalNFTAddress,
      crucible.owner,
      config.christmasTokenId,
      provider
    );
  } else {
    return false;
  }
}

// Get stake data from subgraph
export async function getStakeData(config: Config, crucible: Crucible) {
  const response = await fetch(config.crucibleSubgraphUrl, {
    method: 'POST',
    body: JSON.stringify({
      query: CRUCIBLES_STAKES_QUERY,
      variables: {
        crucibleId: crucible.id
      }
    }),
    headers: {
      'content-type': 'application/json'
    }
  });
  const { data } = (await response.json()) as CrucibleGraphData;
  return data.crucibleEntities;
}

// Get multiplier for individual subscription
export function getSubscriptionMultiplier(
  subscription: Subscription,
  program: ExtendedRewardProgram
) {
  const secondsElapsed = subscription.timeElapsed;
  const daysElapsed = secondsElapsed / 60 / 60 / 24;

  const daysCompleteRatio = Math.min(
    daysElapsed / program.rewardScalingDays,
    1
  );

  const scalingIncrease =
    (program.rewardScalingCeil - program.rewardScalingFloor) *
    daysCompleteRatio;
  const currentScaling = program.rewardScalingFloor + scalingIncrease;
  const multiplier = currentScaling / program.rewardScalingCeil;

  return multiplier;
}

// Get total subscribed amount for individual subscription
const getTotalSubscribedAmount = (subscription: Subscription) => {
  return formatUnits(
    subscription.stakingToken.value.amount,
    subscription.stakingToken.decimals
  );
};

// Get current reward program data
export async function getCurrentRewardProgramsData(
  stakeData: CrucibleEntity[],
  crucible: Crucible,
  chainId: number,
  provider: Provider,
  rewardPrograms: ExtendedRewardProgram[],
  usdValues: UsdValues,
  timestamp: number
) {
  const activeSubscriptionDurations: {
    [rewardProgramAddress: string]: number[];
  } = {};

  const uniqueStakingTokens: { address: string; decimals: number }[] = [];

  let totalSubscribedValue = 0;

  const currentSubscriptions = await Promise.all(
    rewardPrograms.map(async (program) => {
      const subscriptionsData = await Promise.all(
        stakeData.map(async (data) => {
          const locks = data.locks;
          const rewardProgramAddress =
            program.rewardProgramAddress.toLowerCase();
          const filteredLocks = locks.filter(
            (l) => l.delegate.toLowerCase() === rewardProgramAddress
          );

          const subs = await Promise.all(
            filteredLocks.map(async (l) => {
              return await Promise.all(
                l.stakes.map((s) => {
                  const subscriptionDate = s.timestamp;
                  const timeElapsed = timestamp - subscriptionDate;

                  const stakingTokenAddress = l.token.address;
                  const stakingTokenDecimals = l.token.decimals;
                  const stakingTokenSymbol = l.token.symbol;
                  const stakingTokenAmount = s.amount;
                  const stakingTokenAmountUSD =
                    usdValues[ethers.utils.getAddress(stakingTokenAddress)] *
                    Number(
                      formatUnits(stakingTokenAmount, stakingTokenDecimals)
                    );

                  const stakingToken = {
                    value: {
                      amount: stakingTokenAmount,
                      amountUSD: stakingTokenAmountUSD
                    },
                    address: stakingTokenAddress,
                    symbol: stakingTokenSymbol,
                    decimals: Number(stakingTokenDecimals)
                  };

                  return {
                    subscriptionDate,
                    rewardProgramAddress,
                    timeElapsed,
                    stakingToken
                  };
                })
              );
            })
          );

          const subsFlat = subs.flat();
          return subsFlat;
        })
      );

      const subscriptionsDataFlat = subscriptionsData.flat();

      const rewardProgramAddress = program.rewardProgramAddress;
      const { maintainer, name } = program.rewardProgram;
      const { address: stakingTokenAddress, decimals: stakingTokenDecimals } =
        program.rewardProgramTokens.stakingToken;

      if (!uniqueStakingTokens.find((t) => t.address === stakingTokenAddress)) {
        uniqueStakingTokens.push({
          address: stakingTokenAddress,
          decimals: stakingTokenDecimals
        });
      }

      activeSubscriptionDurations[
        program.rewardProgramAddress.toLocaleLowerCase()
      ] = [];

      const subscriptions = subscriptionsDataFlat.map((subscription) => {
        activeSubscriptionDurations[
          program.rewardProgramAddress.toLocaleLowerCase()
        ].push(subscription.timeElapsed);
        return {
          totalAmountSubscribed: getTotalSubscribedAmount(
            subscription as Subscription
          ),
          multiplier: getSubscriptionMultiplier(
            subscription as Subscription,
            program
          ),
          duration: subscription.timeElapsed
        };
      });

      return {
        name,
        maintainer,
        rewardProgramAddress,
        stakingToken: program.rewardProgramTokens.stakingToken,
        multiplierFloor: program.rewardScalingFloor,
        multiplierCeiling: program.rewardScalingCeil,
        subscriptions
      };
    })
  );

  const getTotalSubscribedValue = async () => {
    const tokenAmounts = await Promise.all(
      uniqueStakingTokens.map(async (token) => {
        const tokenContract = new ethers.Contract(
          token.address,
          IUniswapV2ERC20.abi,
          provider
        );
        const balanceBN: BigNumber = await tokenContract.balanceOf(crucible.id);
        const balanceNumber = bigNumberishToNumber(balanceBN, token.decimals);
        const balanceUSD = balanceNumber * usdValues[token.address];
        return balanceUSD;
      })
    );

    return tokenAmounts.reduce((a, b) => a + b, 0);
  };
  totalSubscribedValue = await getTotalSubscribedValue();

  // Aludel v1 or v1.5
  const activeAludelSubs = currentSubscriptions
    .filter((d) => d.name === 'Aludel v1.5' || d.name === 'Aludel v1')
    .map((d) =>
      d.subscriptions.map((sub) => ({
        start: timestamp - sub.duration,
        end: timestamp,
        amount: +(+sub.totalAmountSubscribed).toFixed(8),
        address: d.rewardProgramAddress.toLocaleLowerCase()
      }))
    );

  return {
    currentSubscriptions,
    activeSubscriptionDurations,
    activeAludelSubs,
    totalSubscribedValue
  };
}

export async function getCrucibleHistory(
  rewardPrograms: ExtendedRewardProgram[],
  activeSubscriptionDurations: {
    [rewardProgramAddress: string]: number[];
  },
  crucibleEntities: CrucibleEntity[]
) {
  const crucibleEntity = crucibleEntities[0];
  const crucibleIndex = crucibleEntity.index;
  const crucibleLocks = crucibleEntity.locks;

  // Array of royalty reward program objects
  const featuredRewardPrograms = rewardPrograms.filter(
    (program) => !!program.rewardProgram.royalty
  );

  // Array of royality reward program addresses
  const featuredRewardProgramsList = featuredRewardPrograms.map((filtered) =>
    filtered.rewardProgramAddress.toLocaleLowerCase()
  );

  // Array of featured locks
  const featuredLocks = crucibleLocks.filter((lock) =>
    featuredRewardProgramsList.includes(lock.delegate.toLocaleLowerCase())
  );

  // Get highest multiplier achieved
  const getHighestMultiplierReached = async (
    lock: Lock,
    program: ExtendedRewardProgram
  ) => {
    const longestCompletedSub = Math.max(
      ...lock.unstakes.map((unstake) => unstake.duration),
      0
    );

    const longestCurrentSub = activeSubscriptionDurations[lock.delegate]
      ? Math.max(...activeSubscriptionDurations[lock.delegate], 0)
      : 0;

    // TODO: Subgraph returns incorrect current subs
    // const longestCurrentSub = Math.max(
    //   ...lock.stakes.map((stake) => timestamp - stake.timestamp)
    // );
    const longestDuration = Math.max(longestCompletedSub, longestCurrentSub);

    const longestDurationDays = longestDuration / 60 / 60 / 24;

    const daysCompleteRatio = Math.min(
      longestDurationDays / program.rewardScalingDays,
      1
    );

    const scalingIncrease =
      (program.rewardScalingCeil - program.rewardScalingFloor) *
      daysCompleteRatio;
    const currentScaling = program.rewardScalingFloor + scalingIncrease;
    const multiplier = currentScaling / program.rewardScalingCeil;

    return { multiplier, multiplierProgress: daysCompleteRatio };
  };

  const historicalRewardPrograms = await Promise.all(
    featuredLocks.map(async (lock) => {
      const program = featuredRewardPrograms.find(
        (program) =>
          program.rewardProgramAddress.toLowerCase() ===
          lock.delegate.toLocaleLowerCase()
      );
      const maintainer = program?.rewardProgram.maintainer;
      const rewardProgramAddress = lock.delegate;
      const stakingToken = lock.token.address;
      const { multiplier, multiplierProgress } =
        await getHighestMultiplierReached(lock, program!);

      return {
        maintainer,
        rewardProgramAddress,
        stakingToken,
        multiplier,
        multiplierProgress
      };
    })
  );

  const historicalAludelSubs = featuredLocks
    // TODO: Remove hardcoded address
    .filter(
      (l) =>
        l.delegate === '0x93c31fc68e613f9a89114f10b38f9fd2ea5de6bc' ||
        l.delegate === '0xf0d415189949d913264a454f57f4279ad66cb24d'
    )
    .map((s) =>
      s.unstakes.map((e) => ({
        start: e.timestamp - e.duration,
        end: Number(e.timestamp),
        amount: +(+formatUnits(e.amount, s.token.decimals)).toFixed(8),
        address: s.delegate.toLocaleLowerCase()
      }))
    );

  return {
    historicalRewardPrograms,
    historicalAludelSubs,
    mintNumber: crucibleIndex
  };
}
